/**
 * Author: Paul Grejaldo
 * @todo define fgxObj in php wp_localize_script()
 */
import axios from 'axios';
import md5 from 'md5';
import { memoize, template, isNull, isString } from 'lodash';

export const sendRequest = ( ep, props = null, method = 'POST' ) => {
	const defaultArgs = {
		url         : `${ fgxObj.apiUrl }${ ep }`,
		method      : method || 'POST',
		headers     : { 'X-WP-Nonce' : fgxObj.apiNonce },
		timeout     : 30000,
		dataType    : 'json',
		contentType : 'application/json',
	};

	const args = ( 'get' === method.toLowerCase() )
	             ? { ...defaultArgs, params : props }
	             : { ...defaultArgs, data : props };

	return axios( args )
		.then(
			response => {
				return response;
			}
		)
		.catch(
			error => {
				return ( { error } );
			}
		);
};

/**
 * Usage:
 * Somewhere on the page you have the following markup:
 * <script id="tmpl-my-template" type="text/html">
 *     <div class="some-class">
 *         {{ jsVariableNameObject.whatever }}
 *     </div>
 * </script>
 *
 * then on your component file:
 *
 * // Loads the template
 * const myTemplate = _template( 'my-template', 'jsVariableNameObject' );
 *
 * // Output the template passing the variable for output. Example variable object:
 * const someObjectInAppToBeDisplayed = {
 *     whatever : 'Sample Text'
 * };
 * myTemplate( someObjectInAppToBeDisplayed );
 */
export const _template = memoize( function ( id, varName ) {
	const variable = varName || 'data';
	let compiled,
	    options = {
		    evaluate    : /<#([\s\S]+?)#>/g,
		    interpolate : /\{\{\{([\s\S]+?)\}\}\}/g,
		    escape      : /\{\{([^\}]+?)\}\}(?!\})/g,
		    variable    : variable
	    };

	return function ( data ) {
		compiled = compiled || template( document.getElementById( `tmpl-${ id }` ).innerHTML, options );
		return compiled( data );
	};
} );

/**
 * Create EventSource object
 * @param url
 * @returns object
 */
export const createSource = ( url ) => {
	const source = new EventSource( url );
	let deferred;

	source.onmessage = ( event ) => {
		const json = JSON.parse( event.data );

		if ( deferred ) {
			deferred.resolve( json );
			deferred = null;
		}
	};

	source.onopen = ( event ) => {
		console.log( 'eventsource open' );
	};

	source.onerror = ( event ) => {
		const source = event.target;
		console.log( 'eventsource error', event );
		//source.close();
	};

	return {
		nextMessage() {
			if ( !deferred ) {
				deferred = {};
				deferred.promise = new Promise( ( resolve ) => deferred.resolve = resolve );
			}

			return deferred.promise;
		},
		source
	};
};

export const _localStorage = {
	get : ( key ) => {
		if ( typeof Storage !== 'undefined' ) {
			key = !isString( key ) ? md5( JSON.stringify( key ) ) : key;

			const expiry = localStorage.getItem( `${ key }:ts` );
			if ( expiry && Date.now() > expiry ) {
				localStorage.removeItem( key );
				localStorage.removeItem( `${ key }:ts` );
				return null;
			}

			return localStorage.getItem( key );
		}

		return null;
	},

	set : ( key, value, expiry = 5 ) => {
		if ( typeof Storage !== 'undefined' ) {
			key = !isString( key ) ? md5( JSON.stringify( key ) ) : key;
			localStorage.setItem( key, value );
			expiry = expiry * 60 * 1000;
			if ( 0 < expiry ) {
				expiry += Date.now();
				localStorage.setItem( `${ key }:ts`, expiry );
			}
		}
	},

	getJSON : ( key ) => {
		if ( typeof Storage !== 'undefined' ) {
			key = !isString( key ) ? md5( JSON.stringify( key ) ) : key;
			const expiry = localStorage.getItem( `${ key }:ts` );
			if ( expiry && Date.now() > expiry ) {
				localStorage.removeItem( key );
				localStorage.removeItem( `${ key }:ts` );
				return null;
			}
			return JSON.parse( localStorage.getItem( key ) );
		}

		return null;
	},

	setJSON : ( key, value, expiry = 5 ) => {
		if ( typeof Storage !== 'undefined' ) {
			key = !isString( key ) ? md5( JSON.stringify( key ) ) : key;
			localStorage.setItem( key, JSON.stringify( value ) );
			expiry = expiry * 60 * 1000;
			if ( 0 < expiry ) {
				expiry += Date.now();
				localStorage.setItem( `${ key }:ts`, expiry );
			}
		}
	},
};

export const _sessionStorage = {
	get : ( key ) => {
		if ( typeof Storage !== 'undefined' ) {
			key = !isString( key ) ? md5( JSON.stringify( key ) ) : key;

			const expiry = sessionStorage.getItem( `${ key }:ts` );
			if ( expiry && Date.now() > expiry ) {
				sessionStorage.removeItem( key );
				sessionStorage.removeItem( `${ key }:ts` );
				return null;
			}

			return sessionStorage.getItem( key );
		}

		return null;
	},

	/**
	 * Set string key=>value pair
	 * @param key
	 * @param value
	 * @param expiry
	 */
	set : ( key, value, expiry = 5 ) => {
		if ( typeof Storage !== 'undefined' ) {
			key = !isString( key ) ? md5( JSON.stringify( key ) ) : key;
			sessionStorage.setItem( key, value );
			expiry = expiry * 60 * 1000;
			if ( 0 < expiry ) {
				expiry += Date.now();
				sessionStorage.setItem( `${ key }:ts`, expiry );
			}
		}
	},

	getJSON : ( key ) => {
		if ( typeof Storage !== 'undefined' ) {
			key = !isString( key ) ? md5( JSON.stringify( key ) ) : key;
			const expiry = sessionStorage.getItem( `${ key }:ts` );
			if ( expiry && Date.now() > expiry ) {
				sessionStorage.removeItem( key );
				sessionStorage.removeItem( `${ key }:ts` );
				return null;
			}
			return JSON.parse( sessionStorage.getItem( key ) );
		}

		return null;
	},

	/**
	 * Set key=>object pair
	 * @param key
	 * @param value
	 * @param expiry
	 */
	setJSON : ( key, value, expiry = 5 ) => {
		if ( typeof Storage !== 'undefined' ) {
			key = !isString( key ) ? md5( JSON.stringify( key ) ) : key;
			sessionStorage.setItem( key, JSON.stringify( value ) );
			expiry = expiry * 60 * 1000;
			if ( 0 < expiry ) {
				expiry += Date.now();
				sessionStorage.setItem( `${ key }:ts`, expiry );
			}
		}
	},
};

/**
 * Usage:
 * hashCode( data ).then( hash => { ...do something with `hash` } );
 *
 * @param message
 * @param maxChars
 * @returns {Promise<string>}
 */
export const hashCode = async ( message, maxChars = 8 ) => {
	let code = '';
	if ( 'undefined' !== typeof crypto && 'undefined' !== typeof crypto.subtle && 'undefined' !== typeof crypto.subtle.digest ) {
		const msgUint8   = new TextEncoder().encode( JSON.stringify( message ) ),
		      hashBuffer = await crypto.subtle.digest( 'SHA-256', msgUint8 ),
		      hashArray  = Array.from( new Uint8Array( hashBuffer ) );

		code = hashArray.map( b => b.toString( 16 ).padStart( 2, '0' ) ).join( '' );
	} else {
		code = md5( JSON.stringify( message ) );
	}

	if ( isNull( maxChars ) && maxChars !== code.length ) {
		return code;
	}

	return code.substr( 0, maxChars );
};

export const parseQueryArgs = ( location = null ) => {
	location = location ?? window.location.href;

	return location.substring( location.indexOf( '?' ) + 1 )
		.split( '&' )
		.reduce(
			( memo, param ) => ( {
				...memo,
				[param.split( '=' )[0]] : param.split( '=' )[1]
			} ),
			{}
		);
};
